import { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { ProjectDetailModal } from "./ProjectDetailModal";
import { supabase } from "@/integrations/supabase/client";
import { trackProjectView } from "@/hooks/usePageView";

interface Project {
  id: string;
  project_name: string;
  description?: string;
  technologies?: string[];
  image_url?: string;
  github_url?: string;
  live_demo_url?: string;
}

interface ProjectsSectionProps {
  projects: Project[];
}

interface ProjectWithImage extends Project {
  coverImage?: string;
}

export const ProjectsSection = ({ projects }: ProjectsSectionProps) => {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projectsWithImages, setProjectsWithImages] = useState<ProjectWithImage[]>([]);
  const justOpenedRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    
    const fetchCoverImages = async () => {
      const projectsData = await Promise.all(
        projects.map(async (project) => {
          try {
            const { data } = await supabase
              .from("project_images")
              .select("image_url")
              .eq("project_id", project.id)
              .order("display_order")
              .limit(1)
              .maybeSingle();

            return {
              ...project,
              coverImage: data?.image_url || project.image_url,
            };
          } catch {
            return {
              ...project,
              coverImage: project.image_url,
            };
          }
        })
      );

      if (isMounted) {
        setProjectsWithImages(projectsData);
      }
    };

    if (projects.length > 0) {
      fetchCoverImages();
    }
    
    return () => {
      isMounted = false;
    };
  }, [projects]);

  const openModal = (project: Project) => {
    trackProjectView(project.id);
    justOpenedRef.current = true;
    requestAnimationFrame(() => {
      setSelectedProject(project);
      setTimeout(() => {
        justOpenedRef.current = false;
      }, 100);
    });
  };

  const closeModal = () => {
    if (justOpenedRef.current) {
      return;
    }
    setSelectedProject(null);
  };

  const openLink = (e: React.MouseEvent, url: string) => {
    e.stopPropagation();
    e.preventDefault();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (projects.length === 0) return null;

  const displayProjects = projectsWithImages.length > 0 ? projectsWithImages : projects;

  return (
    <section id="projects" className="section-padding bg-card">
      <div className="max-w-6xl mx-auto container-padding">
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3 animate-fade-in">
          Selected Projects
        </h2>
        <p className="text-muted-foreground mb-10 animate-fade-in" style={{ animationDelay: "100ms" }}>
          A few examples of products and features I've worked on.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {displayProjects.map((project, index) => (
            <Card
              key={project.id}
              className="overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer group animate-fade-in"
              style={{ animationDelay: `${(index + 1) * 100}ms` }}
              onClick={() => openModal(project)}
            >
              <div className="aspect-[16/9] bg-muted/30 flex items-center justify-center border-b border-border overflow-hidden relative">
                {(project as ProjectWithImage).coverImage || project.image_url ? (
                  <>
                    <img 
                      src={(project as ProjectWithImage).coverImage || project.image_url} 
                      alt={project.project_name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/5 transition-colors duration-300 flex items-center justify-center pointer-events-none">
                      <Icon name="open_in_full" size={28} className="text-background opacity-0 group-hover:opacity-80 transition-opacity drop-shadow-lg" />
                    </div>
                  </>
                ) : (
                  <Icon name="rocket_launch" size={44} className="text-muted-foreground/30" />
                )}
              </div>
              
              <div className="p-5">
                <h3 className="text-base font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                  {project.project_name}
                </h3>
                
                {project.description && (
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed line-clamp-2">
                    {project.description}
                  </p>
                )}
                
                {project.technologies && project.technologies.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {project.technologies.slice(0, 3).map((tech, idx) => (
                      <Badge
                        key={idx}
                        variant="success"
                        className="text-xs px-2 py-0.5 rounded-full"
                      >
                        {tech}
                      </Badge>
                    ))}
                    {project.technologies.length > 3 && (
                      <Badge
                        variant="secondary"
                        className="text-xs px-2 py-0.5 rounded-full"
                      >
                        +{project.technologies.length - 3}
                      </Badge>
                    )}
                  </div>
                )}

                {(project.github_url || project.live_demo_url) && (
                  <div className="flex gap-2 pt-3 border-t border-border">
                    {project.github_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-3 text-xs"
                        onClick={(e) => openLink(e, project.github_url!)}
                      >
                        <Icon name="code" size={14} className="mr-1.5" />
                        Code
                      </Button>
                    )}
                    {project.live_demo_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-3 text-xs"
                        onClick={(e) => openLink(e, project.live_demo_url!)}
                      >
                        <Icon name="open_in_new" size={14} className="mr-1.5" />
                        Live Demo
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>

        <ProjectDetailModal
          project={selectedProject}
          isOpen={selectedProject !== null}
          onClose={closeModal}
        />
      </div>
    </section>
  );
};
